/**
 * src/stores/appearance.ts
 * --------------------------
 * Zustand store for org-level appearance/branding configuration.
 *
 * Fully implemented this will:
 *   - Hold config (AppearanceConfig | null) and isLoaded flag.
 *   - loadAppearance(orgId) — calls getAppearance() from src/api/appearance.ts,
 *     stores the config, and applies CSS custom properties to <html>:
 *       --color-primary-{50..900}  derived from primaryColor via tinycolor2
 *       --color-accent-{500,600}   from accentColor
 *       --color-surface            from surface color
 *       --font-family              from fontFamily
 *       --radius-bubble            from borderRadiusBubble
 *       --radius-card              from borderRadiusCard
 *     These variables are read by tailwind.config.ts and component styles.
 *   - applyTheme(config) — imperative CSS variable setter called by loadAppearance.
 *
 * Key dependencies:
 *   zustand, src/api/appearance.ts
 */
import { create } from 'zustand'
import { getAppearance, type AppearanceConfig } from '@/api/appearance'
import { getAppearanceCacheKey, rememberOrgId } from '@/lib/org'

interface AppearanceStore {
  config: AppearanceConfig | null
  isLoaded: boolean

  loadAppearance: (orgId: string) => Promise<void>
  applyTheme: (config: AppearanceConfig) => void
}

// Hydrate the last-known branding synchronously so the very first paint is
// already the admin-controlled design — no flash of the default theme while
// the network round-trip completes. The fetch then refreshes it.
export const useAppearanceStore = create<AppearanceStore>((set, get) => ({
  config: null,
  isLoaded: false,

  loadAppearance: async (orgId: string) => {
    // Mirrored for debugging only; the API client reads the org directly.
    rememberOrgId(orgId)
    const cacheKey = getAppearanceCacheKey(orgId)
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null') as AppearanceConfig | null
      if (cached && cached.orgId === orgId) {
        get().applyTheme(cached)
        set({ config: cached, isLoaded: true })
      }
    } catch { /* ignore bad cache */ }
    try {
      const config = await getAppearance(orgId)
      get().applyTheme(config)
      set({ config, isLoaded: true })
      try { localStorage.setItem(cacheKey, JSON.stringify(config)) } catch { /* quota */ }
    } catch {
      // Fall back to cache/defaults — CSS variables retain their values
      set({ isLoaded: true })
    }
  },

  applyTheme: (config: AppearanceConfig) => {
    const root = document.documentElement
    // Primary color shades (simplified — full implementation uses tinycolor2)
    root.style.setProperty('--color-primary-600', config.primaryColor)
    root.style.setProperty('--color-accent-500', config.accentColor)
    root.style.setProperty('--font-family', config.fontFamily)
    root.style.setProperty('--radius-bubble', config.borderRadiusBubble)
    root.style.setProperty('--radius-card', config.borderRadiusCard)

    if (config.darkModeEnabled) {
      root.classList.add('dark')
    }

    // Favicon
    if (config.faviconUrl) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (link) link.href = config.faviconUrl
    }
  },
}))
